import React, { useState } from "react";
import { toast } from "sonner";
import { BellRing, CheckCircle2, XCircle } from "lucide-react";
import { meta } from "../lib/text";
import api, { formatError } from "../lib/api";
import { useApi } from "../hooks/useApi";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";

function MeetingRow({ m }) {
  return (
    <li className="flex items-center justify-between gap-3 py-2" data-testid={`meeting-detail-${m.id}`}>
      <div><div className="font-medium">{m.title}</div><div className="font-mono-accent text-xs text-muted-foreground">{meta(m.date, m.time, m.location)}</div></div>
      {m.present ? <span className="flex items-center gap-1 text-palm"><CheckCircle2 size={15} /> Present</span>
        : <span className="flex items-center gap-1 text-destructive"><XCircle size={15} /> Absent</span>}
    </li>
  );
}

function MeetingSummary({ data }) {
  return (
    <p className="text-sm text-muted-foreground" data-testid="meeting-detail-summary">
      Attended <b className="font-mono-accent text-foreground">{data.attended}</b> of <b className="font-mono-accent text-foreground">{data.total}</b> meetings
      · <b className={data.percentage >= 50 ? "text-palm" : "text-destructive"}>{data.percentage}%</b>
    </p>
  );
}

function AllAttendedList({ items }) {
  return (
    <div className="border-t border-gold/20 pt-3">
      <p className="eyebrow text-gold">All meetings attended ({items.length})</p>
      <ul className="mt-1 max-h-40 divide-y divide-gold/15 overflow-y-auto text-xs" data-testid="all-attended-list">
        {items.map((m) => (
          <li key={m.id} className="flex items-center justify-between gap-2 py-1.5" data-testid={`all-attended-${m.id}`}>
            <span><span className="font-medium">{m.title}</span> <span className="font-mono-accent text-muted-foreground">{m.date}</span></span>
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${m.counts_for_standing ? "bg-palm/15 text-palm" : "bg-stone-200 text-stone-600"}`}>{m.type_label}</span>
          </li>
        ))}
        {items.length === 0 && <li className="py-2 text-center text-muted-foreground">No check-ins recorded.</li>}
      </ul>
    </div>
  );
}

export function MemberMeetingsDialog({ member, range, onClose }) {
  const q = range.date_from || range.date_to ? `?date_from=${range.date_from}&date_to=${range.date_to}` : "";
  const { data } = useApi(member ? `/admin/reports/attendance/${member.id}/meetings${q}` : null);
  const meetings = data?.meetings || [];
  return (
    <Dialog open={!!member} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="meeting-detail-dialog">
        <DialogHeader><DialogTitle className="font-serif-display text-crimson">{member?.name}</DialogTitle></DialogHeader>
        {data && <MeetingSummary data={data} />}
        <ul className="max-h-60 divide-y divide-gold/15 overflow-y-auto text-sm" data-testid="meeting-detail-list">
          {meetings.map((m) => <MeetingRow key={m.id} m={m} />)}
          {data && meetings.length === 0 && <li className="py-4 text-center text-muted-foreground">No meetings held in this period.</li>}
        </ul>
        {data && <AllAttendedList items={data.all_attended || []} />}
      </DialogContent>
    </Dialog>
  );
}

export function NudgeButton({ filters, below }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reports/attendance/nudge", filters);
      toast.success(`Attendance nudge queued for ${data.queued} member(s)${data.next_meeting ? ` — next meeting ${data.next_meeting.date}` : ""}`);
      setOpen(false);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <>
      <Button size="sm" variant="outline" className="gap-1 border-gold text-crimson" onClick={() => setOpen(true)} data-testid="attendance-nudge"><BellRing size={14} /> Nudge below 50%{below != null && ` (${below})`}</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="nudge-dialog">
          <DialogHeader><DialogTitle className="font-serif-display text-crimson">Send attendance reminder</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">A friendly email, in-app notice and SMS (where a phone is on file) goes to every member in the current filter whose attendance is below 50%, mentioning the next scheduled meeting. Reminders also go out automatically three days before each general meeting.</p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid="nudge-cancel">Cancel</Button>
            <Button disabled={busy} onClick={send} className="bg-crimson hover:bg-crimson-hover" data-testid="nudge-confirm">{busy ? "Sending…" : `Send to ${below ?? ""} member(s)`}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
